import React from 'react';
import { notification } from 'antd';
import { at, get } from 'lodash';
import * as moment from 'moment';
import * as momentTimezone from 'moment-timezone';
import * as yup from 'yup';
import * as Common from 'common/enum/index';
import { homePhonePrefixReg, homePhoneNotPrefixReg } from 'common/enum/regex';
import { ContactModel } from 'model/base';

export const validateFieldSchema = validationSchema => (
  value,
  allValues,
  props,
  name,
) => {
  try {
    validationSchema.validateSyncAt(name, allValues);
    return;
  } catch (err) {
    return err.errors && err.errors[0];
  }
};

export const validatePasswordCommon = (label = 'password') => {
  return yup
    .string()
    .label(label)
    .min(8, `The minimum ${label} length is 8!`)
    .matches(/[a-z]/, `The ${label} must contain one lowercase character!`)
    .matches(/[A-Z]/, `The ${label} must contain one uppercase character!`)
    .matches(/\d/, `The ${label} must contain one number!`)
    .matches(
      /[^A-Za-z0-9]/,
      `The ${label} must contain one special character!`,
    );
};

export const sleep = async time =>
  new Promise(resolve => setTimeout(resolve, time));

export const noop = () => undefined;

export const isPastDate = currentDate =>
  currentDate && currentDate.isBefore(moment().startOf('day'));

export const isFutureDate = currentDate =>
  currentDate && currentDate.isAfter(moment().endOf('day'));

export const disabledDate = currentDate => {
  // Can not select days before today and today
  const afterDate = moment().add(7, 'days');
  const isValid =
    currentDate.isBefore(moment()) || currentDate.isAfter(afterDate);
  return isValid;
};

export const guidGenerator = () => {
  var S4 = function() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return (
    S4() +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    S4() +
    S4()
  );
};

export const checkDuplicateErrorMessages = (desc, item) => {
  return desc.filter(element => at(element, 'props.children')[0] === item);
};

export const getErrorMessages = formSection => {
  if (formSection) {
    const desc = [];
    Object.keys(formSection).forEach(k => {
      const item = formSection[k];
      if (item instanceof Object) {
        return desc.push(getErrorMessages(item));
      } else {
        if (checkDuplicateErrorMessages(desc, item)[0]) return;
        desc.push(<p key={k}>{item}</p>);
      }
    });
    if (desc && desc.length > 0) {
      return desc;
    }
  }
};

export const notifyFormSchedulerErrors = formErrors => {
  const desc = [];

  if (formErrors) {
    Object.keys(formErrors).forEach(errorIndex => {
      if (typeof formErrors[errorIndex] !== 'object') {
        desc.push(<p key={errorIndex}>{formErrors[errorIndex]}</p>);
      } else {
        formErrors[errorIndex].map(item => {
          if (item)
            Object.keys(item).forEach(errorIndex => {
              desc.push(<p key={errorIndex}>{item[errorIndex]}</p>);
            });
          return true;
        });
      }
    });
  }
  if (desc && desc.length > 0) {
    notification.error({
      message: 'Form is invalid',
      description: desc,
    });
  }
};

export const notifyFormErrors = formErrors => {
  if (formErrors) {
    const desc = getErrorMessages(formErrors);

    if (desc && desc.length > 0) {
      notification.error({
        message: 'Form is invalid',
        description: desc,
      });
    }
  }
};

export const buildValidationSchema = model => {
  const map = {};
  Object.keys(model).forEach(e => {
    const field = model[e];
    if (field.validate) {
      map[field.name] = field.validate;
    }
  });
  return yup.object(map);
};

// Add timezone offset to unix time
export const modifyUnixTimeBaseOnTimezoneOffset = (time, offset) => {
  try {
    return time + offset * 60 * 60;
  } catch (error) {
    return false;
  }
};

//From Date in seconds to format
export const convertDateToFormat = (date, format = Common.Format.Date) => {
  try {
    return moment(date * Common.TimeEnum.TimeUnixMillisecond).format(format);
  } catch (error) {
    return false;
  }
};

//From Date in format to ISO string
export const convertDateToISOFormat = (date, format = Common.Format.Date) => {
  try {
    return moment.utc(date, format).toISOString();
  } catch (error) {
    return false;
  }
};

//From Date in format to seconds
export const convertDateFromFormat = (date, format = Common.Format.Date) => {
  try {
    return moment(date).unix();
  } catch (error) {
    return false;
  }
};

//From ISO string to date format
export const convertISOFormatToDate = (date, format = Common.Format.Date) => {
  if (!date) return false;
  try {
    return moment(date)
      .utc()
      .format(format);
  } catch (error) {
    return false;
  }
};

//From Time in seconds to format
export const convertTimeToFormat = (time, format = Common.Format.Time) => {
  try {
    let seconds = time / 86400;
    const days = Math.floor(seconds / (3600 * 24));
    seconds -= days * 3600 * 24;
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    return moment(`${hours}:${minutes}:${seconds}`, 'HH:mm:ss').format(format);
  } catch (error) {
    return false;
  }
};

//From Time in seconds to Moment Object
export const convertTimeToMomentObj = time => {
  try {
    if (time) {
      let seconds = time;
      const days = Math.floor(seconds / (3600 * 24));
      seconds -= days * 3600 * 24;
      const hours = Math.floor(seconds / 3600);
      seconds -= hours * 3600;
      const minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      return moment(`${hours}:${minutes}:${seconds}`, 'HH:mm:ss');
    }
    return null;
  } catch (error) {
    return false;
  }
};

//From format to Time in seconds
export const convertTimeFromFormat = (time, format = Common.Format.Time) => {
  try {
    if (time) {
      const timeObj = moment(time, format);
      const unixTime =
        timeObj.get('hours') * 60 * 60 +
        timeObj.get('minutes') * 60 +
        timeObj.get('seconds');
      return unixTime;
    }
    return null;
  } catch (error) {
    return 0;
  }
};

export const convertTimeToUnixWithSpecifyTimezone = (
  unixTime,
  offset = 0,
  format = Common.Format.Time,
) => {
  if (unixTime) {
    try {
      return moment
        .unix(unixTime)
        .utcOffset(offset)
        .utc(offset)
        .unix();
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const setPagingPage = ({ offset = 0, limit = 0, total = 0 } = {}) => ({
  current: limit !== 0 ? Math.round(offset / limit) + 1 : 1,
  pageSize: limit,
  total,
  offset,
});
export const getHoursMinutes = (startTime, endTime) => {
  const startHours = parseInt(moment(startTime, 'HH:mm').format('HH'));
  const endHours = parseInt(moment(endTime, 'HH:mm').format('HH'));

  const startMinute = parseInt(moment(startTime, 'HH:mm').format('mm'));
  const endMinute = parseInt(moment(endTime, 'HH:mm').format('mm'));

  return { startHours, endHours, startMinute, endMinute };
};

//get start date and end date in month
export const getStartAndEndDay = (date, id, view) => {
  const start =
    moment(date)
      .subtract(1, view)
      .startOf(view)
      .startOf('day')
      .toDate()
      .getTime() / Common.TimeEnum.TimeUnixMillisecond;
  const end =
    moment(date)
      .add(1, view)
      .endOf(view)
      .startOf('day')
      .toDate()
      .getTime() / Common.TimeEnum.TimeUnixMillisecond;
  return { start, end, staffId: id };
};

export const stripTime = date => {
  date = moment(date);
  date.hours(0);
  date.minutes(0);
  date.seconds(0);
  date.milliseconds(0);
  return date;
};

export const convertUnixToHoursMinute = unix_timestamp => {
  const timeObject = moment.unix(unix_timestamp);
  return timeObject.local().format('HH:mm');
};

export const filteredObjByKey = (raw, allowed) => {
  return Object.keys(raw)
    .filter(key => allowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

export const convertUnixToISOIgnoreTimezone = timestamp => {
  const date = new Date(timestamp * Common.TimeEnum.TimeUnixMillisecond);
  const isoDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  ).toISOString();
  return isoDate;
};

export const convertToUnixBySpecifyTimezone = (moment, offset) => {};

export const secondsToHms = time => {
  time = Number(time);
  var h = Math.floor(time / 3600);
  var m = Math.floor((time % 3600) / 60);
  var s = Math.floor((time % 3600) % 60);

  var hours = h > 0 ? h : 0;
  var minutes = m > 0 ? m : 0;
  var seconds = s > 0 ? s : 0;
  return { hours, minutes, seconds };
};

export const calculateAgeFromISOFormat = dob => {
  try {
    if (!dob) return null;
    let patientDOB = moment(dob);
    let years = moment().diff(patientDOB, 'years');
    return years;
  } catch (error) {
    return false;
  }
};

export const checkNumberLengthNormalize = (length, parseToInt = true) => (
  value,
  previousValue,
) => {
  const result =
    value && ((length && value.length > length) || value.match(/\D/))
      ? previousValue
      : value;
  if (parseToInt && result) {
    return Number(result);
  } else {
    return result;
  }
};

export const checkPhoneNumberLengthNormalize = length => (
  value,
  previousValue,
) => {
  let result;
  const indexOfString = 1;
  if (value && value.length === indexOfString) {
    result =
      value && value.match(/\D/) && !value.match(homePhonePrefixReg)
        ? previousValue
        : value;
    return result;
  } else {
    if (value) {
      const valueRemovedFirstString = value.slice(indexOfString, value.length);
      const valueFirstString = value[0];
      if (!valueFirstString.match(homePhonePrefixReg) && value.match(/\D/)) {
        result = previousValue;
      } else {
        result = valueRemovedFirstString.match(/\D/) ? previousValue : value;
      }
    }
  }
  return result || value;
};

export const getFormFieldName = value => value.split('.').pop();

export const getRequireFormField = value => value.split(/\(?\*\)?/)[0];

export const convertUnixToDateWithTimezone = (
  unixTime,
  timeZone = Common.AUS_TIME_ZONE.UTC800.area,
  format = Common.Format.Time,
) => {
  const date = momentTimezone.unix(unixTime);
  const dateWithSiteTimeZone = date.tz(timeZone).format(format);
  return dateWithSiteTimeZone;
};

export const convertUnixToFormatWithTimezone = (
  unixTime,
  timeZone = Common.AUS_TIME_ZONE.UTC800.area,
  format = Common.Format.Time,
) => {
  if (unixTime) {
    const date = momentTimezone.unix(unixTime);
    const dateWithSiteTimeZone = date.tz(timeZone);
    return dateWithSiteTimeZone;
  }
  return null;
};

export const convertToStarOfDateFromFormat = (
  date,
  format = Common.Format.Date,
) => {
  try {
    return moment(date)
      .startOf('day')
      .unix();
  } catch (error) {
    return false;
  }
};

export const convertToEndOfDateFromFormat = (
  date,
  format = Common.Format.Date,
) => {
  try {
    return moment(date)
      .endOf('day')
      .unix();
  } catch (error) {
    return false;
  }
};

export const parseNumber = value => value && parseInt(value);

export const getEmailResultByType = (addressBook, emailType) => {
  const receivingEmails = addressBook
    .filter(item => item.communicationTypeId === emailType)
    .map(i => {
      return {
        key: i.uuid,
        label: i.email,
      };
    });
  return receivingEmails;
};

export const isArray = value => {
  return value && typeof value === 'object' && value.constructor === Array;
};

export const trim = (str, char = ' ') => {
  if (str) {
    while (str.indexOf(`${char}${char}`) >= 0) {
      str = str.replace(`${char}${char}`, char);
    }
  }
  return str;
};

export const isObjectEmpty = obj => {
  if (obj) {
    return Object.keys(obj).length === 0;
  } else {
    return true;
  }
};

export const getContactUpdateObject = contactRequired => {
  const _phoneHome = get(contactRequired, ContactModel.phoneHomeF.name);

  const _phoneHomePrefix = get(
    contactRequired,
    ContactModel._phoneHomePrefixF.name,
  );
  if (_phoneHome) {
    return `${_phoneHomePrefix || ''}${_phoneHome}`;
  }
  return '';
};

export const getHomePhonePrefix = homePhone => {
  function _checkHomePhonePrefixMatchPhonePrefixList(_homePhonePrefixTemp) {
    return Common.PHONE_PREFIX_LIST.find(item => item === _homePhonePrefixTemp);
  }

  function getObjectHomePhone(_homePhonePrefixTemp, homePhone, length) {
    let _homePhonePrefix, _homePhoneSub;

    if (_checkHomePhonePrefixMatchPhonePrefixList(_homePhonePrefixTemp)) {
      _homePhonePrefix = _homePhonePrefixTemp;
      _homePhoneSub = homePhone.slice(length, homePhone.length);
    }
    return { _homePhonePrefix, _homePhoneSub };
  }

  let homePhoneObject = {};

  const LENGTH_OF_HEAD_HAVE_PREFIX = 3;
  const LENGTH_OF_HEAD_NOT_HAVE_PREFIX = 2;

  if (homePhone) {
    if (homePhone.match(homePhonePrefixReg)) {
      let _homePhonePrefixTemp = homePhone.slice(0, LENGTH_OF_HEAD_HAVE_PREFIX);
      homePhoneObject = getObjectHomePhone(
        _homePhonePrefixTemp,
        homePhone,
        LENGTH_OF_HEAD_HAVE_PREFIX,
      );
    }
    if (homePhone.match(homePhoneNotPrefixReg)) {
      let _homePhonePrefixTemp = `+${homePhone.slice(
        0,
        LENGTH_OF_HEAD_NOT_HAVE_PREFIX,
      )}`;
      homePhoneObject = getObjectHomePhone(
        _homePhonePrefixTemp,
        homePhone,
        LENGTH_OF_HEAD_NOT_HAVE_PREFIX,
      );
    }
  } else {
    homePhoneObject._homePhonePrefix = Common.PHONE_PREFIX_DEFAULT;
  }

  return homePhoneObject;
};
